import { QueryClient, useQuery } from '@tanstack/react-query';

import {
  type GcpServiceAccountForList,
  gcpServiceAccountQueries,
} from '@/lib/queries/gcpServiceAccount';
import {
  type AwsCredentialsForList,
  awsCredentialsQueries,
} from '@/lib/queries/integrations/cloud/aws/credentials';
import {
  type AwsRole,
  awsRoleQueries,
} from '@/lib/queries/integrations/cloud/aws/roles';
import {
  type DatadogApiCredentialsForList,
  monitoringDatadogQueries,
} from '@/lib/queries/integrations/monitoring/datadog';

export const preLoadIntegrations =
  (projectId: string) =>
  ({
    context: { queryClient },
  }: {
    context: { queryClient: QueryClient };
  }): void => {
    queryClient.prefetchQuery(awsCredentialsQueries.list(projectId));
    queryClient.prefetchQuery(awsRoleQueries.list(projectId));
    queryClient.prefetchQuery(gcpServiceAccountQueries.list(projectId));
    queryClient.prefetchQuery(monitoringDatadogQueries.list(projectId));
  };

export const useIntegrations = (
  projectId: string,
):
  | {
      isLoading: true;
      noData: undefined;
      awsCredentials?: undefined;
      awsRoles?: undefined;
      gcpServiceAccounts?: undefined;
      datadogApiCredentials?: undefined;
    }
  | {
      isLoading: false;
      awsCredentials: AwsCredentialsForList[];
      awsRoles: AwsRole[];
      gcpServiceAccounts: GcpServiceAccountForList[];
      datadogApiCredentials: DatadogApiCredentialsForList[];
      noData: boolean;
    } => {
  const awsCredentials = useQuery(awsCredentialsQueries.list(projectId));
  const awsRoles = useQuery(awsRoleQueries.list(projectId));

  const gcpServiceAccounts = useQuery(gcpServiceAccountQueries.list(projectId));

  const datadogApiCredentials = useQuery(
    monitoringDatadogQueries.list(projectId),
  );

  if (
    awsCredentials.data === undefined ||
    awsRoles.data === undefined ||
    gcpServiceAccounts.data === undefined ||
    datadogApiCredentials.data === undefined
  ) {
    return { isLoading: true, noData: undefined };
  }

  const noData =
    awsCredentials.data.filter(({ status }) => status === 'active').length ===
      0 &&
    awsRoles.data.filter(({ status }) => status === 'active').length === 0 &&
    gcpServiceAccounts.data.filter(({ status }) => status === 'active')
      .length === 0 &&
    datadogApiCredentials.data.filter(({ status }) => status === 'active')
      .length === 0;

  return {
    isLoading: false,
    awsCredentials: awsCredentials.data,
    awsRoles: awsRoles.data,
    gcpServiceAccounts: gcpServiceAccounts.data,
    datadogApiCredentials: datadogApiCredentials.data,
    noData,
  };
};
